import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import BookkeepingProgressionViewModel, {
  BookkeepingTrackerCardTab as Tab,
} from '../BookkeepingProgressionCard/BookkeepingProgressionViewModel'
import {
  Card,
  CardContent,
  CardHeader,
  CardTabs,
  CardTitle,
} from 'components/Card'
import {
  BookkeepingCycle,
  WorkItem,
} from 'jobs/bookkeeping/BookkeepingProgressionInfo'
import { TabBar, TabItem } from 'components/TabBar'
import { match } from 'utils/pattern-match-util'
import Button from 'components/Button'
import {
  formatShortDate,
  formatCycleToShortDate,
} from '../BookkeepingSelectCycleDialog/formatters'
import { JobCardEmptyState } from '../JobCardEmptyState'
import styles from './BookkeepingTrackerCard.sass'

interface BookkeepingTrackerCardProps {
  vm: BookkeepingProgressionViewModel
}

export default observer(function BookkeepingTrackerCard({
  vm,
}: BookkeepingTrackerCardProps) {
  return vm.canViewBookkeepingCyclesTab ? (
    <Card>
      <CardHeader compact>
        <CardTitle cardLayout>Bookkeeping tracker</CardTitle>
      </CardHeader>
      <CardTabs>
        <TabBar>
          <TabItem
            cardLayout
            active={
              vm.bookkeepingTrackerCardTabController.current.tab ===
              Tab.FinishedItems
            }
            onClick={() =>
              vm.bookkeepingTrackerCardTabController.change(Tab.FinishedItems)
            }
          >
            Finished items
          </TabItem>
          <TabItem
            cardLayout
            active={
              vm.bookkeepingTrackerCardTabController.current.tab === Tab.Cycles
            }
            onClick={() =>
              vm.bookkeepingTrackerCardTabController.change(Tab.Cycles)
            }
          >
            Cycles
          </TabItem>
        </TabBar>
      </CardTabs>
      <CardContent scroll>
        {match(vm.bookkeepingTrackerCardTabController.current.tab, {
          [Tab.FinishedItems]: () => <FinishedItemsList vm={vm} topPadding />,
          [Tab.Cycles]: vm.anyCycles
            ? () => <CyclesList vm={vm} />
            : () => (
                <JobCardEmptyState helpText="No cycles available." topPadding />
              ),
        })}
      </CardContent>
    </Card>
  ) : (
    <FinishedItemsCard vm={vm} />
  )
})
const FinishedItemsCard = observer(function FinishedItemsCard({
  vm,
}: BookkeepingTrackerCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Finished items</CardTitle>
      </CardHeader>
      <CardContent>
        <FinishedItemsList vm={vm} />
      </CardContent>
    </Card>
  )
})

interface FinishedItemsListProps {
  vm: BookkeepingProgressionViewModel
  topPadding?: boolean
}

const FinishedItemsList = observer(function FinishedItemsList({
  vm,
  topPadding,
}: FinishedItemsListProps) {
  return (
    <div
      className={cx(styles.finishedItemsList, topPadding && styles.topPadding)}
    >
      {vm.workItems?.map((wi) => (
        <FinishedItem key={wi.workItemId} item={wi} />
      ))}
    </div>
  )
})

interface FinishedItemProps {
  item: WorkItem
}

const FinishedItem = observer(function FinishedItem({
  item,
}: FinishedItemProps) {
  return (
    <div className={styles.finishedItem}>
      <div className={styles.finishedItemName}>{item.inventoryItemName}</div>
      <div className={styles.finishedItemCount}>
        {item.completedCount} of {item.quantity} completed
      </div>
    </div>
  )
})

interface CyclesListProps {
  vm: BookkeepingProgressionViewModel
}

const CyclesList = observer(function CyclesList({ vm }: CyclesListProps) {
  return (
    <div className={styles.cyclesItemsList}>
      {vm.bookkeepingProgressionInfo?.cycles
        .map((cycle) => <CycleItem key={cycle.id} cycle={cycle} vm={vm} />)
        .toReversed()}
    </div>
  )
})

interface CycleItemProps {
  cycle: BookkeepingCycle
  vm: BookkeepingProgressionViewModel
}

const CycleItem = observer(function CycleItem({ cycle, vm }: CycleItemProps) {
  return (
    <div className={styles.cycleItem}>
      <div className={styles.cycleStatus}>
        {cycle.cycleStatusName} &bull; Cycle {cycle.number}
      </div>
      <div className={styles.cycleDateContainer}>
        <div className={styles.cycleDate}>
          {cycle.completedAt
            ? formatCycleToShortDate(cycle)
            : formatShortDate(cycle.startedAt)}
        </div>
        <Button
          type="text"
          size="baseline"
          onClick={() => vm.bookkeepingCycleDetailsVM.open(cycle)}
        >
          View
        </Button>
      </div>
    </div>
  )
})
